/**
 * `/crm/persons/:id` — one person: how to reach them, where they work, their
 * deals, and what is planned with them.
 */
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { AsyncContent } from '../../components/AsyncContent';
import { Columns } from '../../components/Columns';
import { Details } from '../../components/Details';
import { EntityDetail } from '../../components/EntityDetail';
import { Panel } from '../../components/Panel';
import { useAsync } from '../../hooks/useAsync';
import { usePersonActions } from './actions';
import { ActivityPanel } from './ActivityPanel';
import { getPerson } from './api';
import { RelatedDeals } from './RelatedDeals';

export default function PersonPage() {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  const id = Number(useParams()['id']);
  const person = useAsync(() => getPerson(id), [id]);
  const actions = usePersonActions({
    onChanged: (key) => (key === 'delete' ? navigate('/crm/persons') : person.reload()),
  });

  return (
    <AsyncContent state={person}>
      {({ person: record }) => (
        <EntityDetail
          backTo="/crm/persons"
          backLabel={t('person.back')}
          title={record.name}
          description={record.organization?.name}
          entity={actions.forRow(record)}
          error={actions.error}
        >
          <Columns split={[1, 1.4]} align="start">
            <Stack spacing={3}>
              <Panel>
                <Details
                  rows={[
                    [
                      t('field.email'),
                      record.email && (
                        <Link href={`mailto:${record.email}`} underline="hover">
                          {record.email}
                        </Link>
                      ),
                    ],
                    [
                      t('field.phone'),
                      record.phone && (
                        <Link href={`tel:${record.phone}`} underline="hover">
                          {record.phone}
                        </Link>
                      ),
                    ],
                    [
                      t('field.organization'),
                      record.organization && (
                        <Link component={RouterLink} to={`/crm/organizations/${record.organization.id}`} underline="hover">
                          {record.organization.name}
                        </Link>
                      ),
                    ],
                    [t('field.owner'), record.owner.name],
                  ]}
                />
              </Panel>
              <RelatedDeals filter={{ personId: record.id }} />
            </Stack>
            <ActivityPanel filter={{ personId: record.id }} link={{ person: { id: record.id, name: record.name } }} />
          </Columns>
        </EntityDetail>
      )}
    </AsyncContent>
  );
}
